/**
 * Translation rules that exist because this is a medical application.
 *
 * A mistranslated button label is an annoyance. A mistranslated dose, drug name or
 * frequency is a patient-safety event. These helpers make the safe thing the easy thing,
 * and the tests in `__tests__/clinical-safety.test.ts` turn each rule into a build gate.
 *
 * The rules:
 *
 *  1. NEVER TRANSLATE clinical identifiers. Drug brand and generic names, strengths,
 *     units (mg, mL, mmHg), frequency codes (BD, TID, HS), lab test and analyte names,
 *     ICD-10/SNOMED terms, allergen names, and any free text a patient or clinician
 *     typed. These arrive from the API and render verbatim in every language. Only the
 *     chrome *around* them is translated.
 *
 *  2. CLINICAL TERMS SHOWN TO PATIENTS ARE BILINGUAL, never Telugu-only. A patient
 *     reading "రక్తపోటు" on screen still has to match it against an English lab report,
 *     a pharmacy label and a referral letter. `clinicalTerm()` guarantees the English
 *     canonical term is always present.
 *
 *  3. LATIN DIGITS ALWAYS. Telugu numerals (౦-౯) must never appear in a dose, vital,
 *     date, amount, phone number or identifier. Locales pin `-u-nu-latn` and
 *     `assertLatinDigits()` catches any that slip through.
 */

/** Telugu digits ౦-౯ (U+0C66–U+0C6F), plus the Devanagari set for the same reason. */
const NON_LATIN_DIGIT_RE = /[౦-౯०-९٠-٩۰-۹]/;

export function containsNonLatinDigits(value: string): boolean {
  return NON_LATIN_DIGIT_RE.test(value);
}

/**
 * Throws in dev, logs in production. Used by the formatters in `format.ts` so a numbering
 * system regression surfaces at the point of formatting rather than on a printed prescription.
 */
export function assertLatinDigits(value: string, context: string): string {
  if (containsNonLatinDigits(value)) {
    const message =
      `[i18n] Non-Latin digits in "${context}": ${JSON.stringify(value)}. ` +
      `Doses, vitals, dates and amounts must always render in Latin digits (0-9).`;
    if (import.meta.env.DEV) throw new Error(message);
    console.error(message);
  }
  return value;
}

/**
 * Render a clinical term for display.
 *
 * `english` is the canonical term and is never omitted. In English it is returned alone;
 * in any other language the translated gloss leads and the English follows in parentheses.
 * If the gloss is missing the English term stands on its own — a missing translation
 * degrades to "correct but untranslated", never to "blank" or "wrong".
 *
 *   clinicalTerm('రక్తపోటు', 'Blood Pressure')  ->  'రక్తపోటు (Blood Pressure)'
 *   clinicalTerm(undefined,  'Blood Pressure')  ->  'Blood Pressure'
 */
export function clinicalTerm(translated: string | undefined | null, english: string): string {
  const gloss = translated?.trim();
  if (!gloss || gloss === english) return english;
  return `${gloss} (${english})`;
}

/**
 * Field paths whose values come from the API and must be rendered verbatim, whatever the
 * UI language. Consulted by the lint rule and documented here as the single source of truth
 * for reviewers: if you find yourself wanting to pass one of these through `t()`, stop.
 */
export const NEVER_TRANSLATE_FIELDS = [
  'medicationName',
  'brandName',
  'genericName',
  'drugName',
  'strength',
  'dosage',
  'dose',
  'frequencyCode',
  'route',
  'unit',
  'testName',
  'analyteName',
  'loincCode',
  'icdCode',
  'snomedCode',
  'allergen',
  'allergyName',
  'diagnosis',
  'chiefComplaint',
  'clinicalNotes',
  'notes',
  'remarks',
  'patientName',
] as const;
